/**
 * Four headline KPI cards for the client dashboard:
 *   Total Portfolio · Accumulated Yield · Active Products · Next Maturity
 *
 * Designed to sit in a 2×2 grid on mobile, 4-across on ≥ md.
 * All amounts are formatted via Intl so they respect the client's
 * locale (important for US / CA clients seeing USD / CAD figures).
 */
import { formatDate, formatMoney } from '../../lib/datetime';
import { calendarDate } from './enteringFund.helpers';
import { pickCurrencyBalance } from '../../lib/portfolioSummary.helpers';
import { colors, gradients } from '../../styles/theme';
import type { PortfolioSummary } from '../../types/finance';
import RiskDisclaimer from '../RiskDisclaimer';


interface Props {
  summary: PortfolioSummary;
  currency: string;
}

interface CardProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}

function StatCard({ label, value, sub, accent = false }: Readonly<CardProps>): JSX.Element {
  return (
    <div
      style={{
        background: accent ? gradients.navy : colors.surface,
        border: `1px solid ${accent ? 'transparent' : colors.border}`,
        borderRadius: 16,
        padding: '20px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: accent ? colors.gold : colors.muted,
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 700,
          color: accent ? '#FFFFFF' : colors.ink,
          lineHeight: 1.2,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </p>
      {sub && (
        <p style={{ margin: 0, fontSize: 12, color: accent ? 'rgba(255,255,255,0.5)' : colors.muted }}>
          {sub}
        </p>
      )}
    </div>
  );
}

export default function PortfolioSummaryCards({ summary, currency }: Readonly<Props>): JSX.Element {
  // PORTAL-01: `summary.balances` is kept separate per currency — pick the
  // dashboard's primary display currency's row (see helper docstring).
  const balance = pickCurrencyBalance(summary.balances, currency);
  const totalBalance = formatMoney(balance.total_balance, currency);
  const yieldAmt = formatMoney(balance.accumulated_yield, currency);

  const balanceNum = Number(balance.total_balance);
  const yieldNum = Number(balance.accumulated_yield);
  // What the client's capital has earned so far, net of any losing period: the
  // sum of the shares of each period's real result. Never an annualized rate.
  const effectivePct =
    balanceNum > 0 && yieldNum !== 0
      ? `${((yieldNum / balanceNum) * 100).toFixed(2)}% of your balance, net of losses`
      : 'Shared with you to date';

  const nextMaturity = summary.upcoming_maturities[0];
  const nextMaturityLabel = nextMaturity
    ? calendarDate(nextMaturity.maturity_date)
    : '—';
  const nextMaturitySub = nextMaturity
    ? [
        nextMaturity.product_name,
        formatMoney(nextMaturity.amount, nextMaturity.currency_code),
      ].join(' · ')
    : 'No term ends in 60 days';

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
        }}
      >
        <StatCard
          label="Total balance"
          value={totalBalance}
          sub={`Active series: ${summary.active_products_count}`}
          accent
        />
        <StatCard
          label="Results to date"
          value={yieldAmt}
          sub={effectivePct}
        />
        <StatCard
          label="Active commitments"
          value={String(summary.active_commitments_count)}
          sub={
            summary.next_commitment_date
              ? `Next: ${formatDate(summary.next_commitment_date)}`
              : 'No active commitments'
          }
        />
        <StatCard
          label="Next term complete after"
          value={nextMaturityLabel}
          sub={nextMaturitySub}
        />
      </div>
      {/* The canonical disclaimer rides under the grid: the figures are informational,
          never a promised rate. */}
      <RiskDisclaimer compact />
    </div>
  );
}
